import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  StyledDarkRedInformationTypography,
  StyledInfoBox,
  StyledInformationSpan,
  StyledMainBox,
} from "../../../components/commonComponents/StyledComponents/styled";
import TextInputField from "../../../components/commonComponents/TextInputs/TextInputField";
import DateInputField from "../../../components/commonComponents/TextInputs/DateInputs";
import { Close, Save } from "@mui/icons-material";
import CloseButton from "../../../components/commonComponents/Buttons/CloseButton";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import {
  checkUserRole,
  fetchStoreLevelCompanies,
  checkUserDetailsFromCookie,
  fetchBrands,
} from "../../../api_calls/userManagement";
import { useDispatch, useSelector } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import { saveCampaignsInformation } from "../../../api_calls/campaignRequests";
import SingleSelect from "../../../components/commonComponents/Selectors/SingleSelect";
import { formatToStandardDate, standardFormatToday, clampDate } from "../../../api_calls/utils";
import moment from "moment-timezone";
import MultiSelect from "../../../components/commonComponents/Selectors/MultiSelect";
import platformConfig from "../../../platformConfig";

const AddNewCampaign = ({ open, onClose, size, usedFor, closeModal, campaignRecord }) => {
  const currentStateData = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [showManualBrandCompany, setShowManualBrandCompany] = React.useState(false);
  const [showStartDateConfirmation, setStartDateConfirmation] = React.useState(false);
  const [showEndDateConfirmation, setStartEndConfirmation] = React.useState(false);
  const [dialogExpanded, setDialogExpanded] = React.useState(false);

  const checkUserDetails = checkUserDetailsFromCookie();
  let userDetailObj = {};
  if (checkUserDetails.success === true) {
    userDetailObj = checkUserDetails.user_detail;
  }
  const [userInfo, setUserInfo] = React.useState(userDetailObj);

  let availableToUserTypes = platformConfig.availableToUserTypes;

  const checkRole = checkUserRole();

  const handleFieldChange = (campaignData) => {
    const selectedCampaignData =
      typeof currentStateData.new_campaign_data !== "undefined" ? currentStateData.new_campaign_data : {};

    dispatchAction(
      updateStateData({
        new_campaign_data: { ...selectedCampaignData, ...campaignData },
      })
    );
  };

  // Week selection handler - sets both start and end date
  const handleWeekSelect = React.useCallback((monday, sunday, fieldRole) => {
    const today = standardFormatToday();

    const isStartDisabled =
      typeof currentStateData.new_campaign_data !== "undefined" &&
      typeof currentStateData.new_campaign_data.start_status !== "undefined" &&
      ["started_today", "started_days_ago"].includes(currentStateData.new_campaign_data.start_status);

    const isEndDisabled =
      typeof currentStateData.new_campaign_data !== "undefined" &&
      typeof currentStateData.new_campaign_data.end_status !== "undefined" &&
      ["ended_days_ago"].includes(currentStateData.new_campaign_data.end_status);

    if (isStartDisabled && isEndDisabled) {
      return;
    }

    // Mirrors the start date field's minSelectable
    const startFieldMin = today;
    const clampedStart = clampDate(monday, startFieldMin, null);

    // Mirrors the end date field's minSelectable IIFE:
    // if the resolved start is today or later, end must be >= start
    const endFieldMin = clampedStart >= today ? clampedStart : today;
    const clampedEnd = clampDate(sunday, endFieldMin, null);

    if (clampedStart > clampedEnd) {
      return;
    }

    const updateData = {};

    if (fieldRole === "end") {
      // Week selected from the end date field: anchor end to Sunday, derive start from Monday
      if (!isEndDisabled) {
        updateData.campaign_end_date = clampedEnd;
      }
      if (!isStartDisabled) {
        updateData.campaign_start_date = clampedStart;
      }
    } else {
      // Week selected from the start date field: anchor start to Monday, derive end from Sunday
      if (!isStartDisabled) {
        updateData.campaign_start_date = clampedStart;
      }
      if (!isEndDisabled) {
        updateData.campaign_end_date = clampedEnd;
      }
    }

    if (Object.keys(updateData).length > 0) {
      handleFieldChange(updateData);
    }
  }, [currentStateData.new_campaign_data]);

  // Global min/max for week selection styling
  const weekSelectionMin = standardFormatToday();
  const weekSelectionMax = null; // No upper limit for campaigns

  const setCampaignStartDateConstraint = () => {
    return standardFormatToday();
    // const today = standardFormatToday();
    // const nextDay = new Date(today);
    // nextDay.setDate(today.getDate() + 1);
    // return nextDay;
  };

  React.useEffect(() => {

    // auto select cyreeb gmbh as default ownner 
    if (userInfo.user_type === "cyreen" && usedFor !== "update") {
      if (typeof currentStateData.independent_owners_list !== "undefined") {
        let data = currentStateData.independent_owners_list.find((owner) => owner.label.includes("Cyreen"));
        if (data !== undefined) {
          handleFieldChange({
            campaign_owner: data.label,
            campaign_owner_id: data.id,
          });
        }
      }
    }
    // pre fill campaign data if used for update
    if (typeof campaignRecord !== "undefined" && Object.keys(campaignRecord).length !== 0) {
      dispatchAction(
        updateStateData({
          new_campaign_data: {
            campaign_id: campaignRecord.campaign_id,
            campaign_name: campaignRecord.campaign_name,
            brand_name: campaignRecord.brand,
            campaign_start_date: formatToStandardDate(campaignRecord.campaign_start_date),
            campaign_end_date: formatToStandardDate(campaignRecord.campaign_end_date),
            campaign_retailer_company: campaignRecord.retailer_company_name,
            campaign_brand_company: campaignRecord.brand_company_name,
            campaign_retailer_company_id: campaignRecord.retailer_company_id,
            campaign_brand_company_id: campaignRecord.brand_company_id,
            report_status: campaignRecord.campaign_status_code === 4 ? true : false,
            campaign_report_status_code: campaignRecord.campaign_status_code,
            start_status: campaignRecord.campaign_start_status,
            end_status: campaignRecord.campaign_end_status,
          },
        })
      );
      // fetch store companies.
      fetchStoreLevelCompanies("campaigns", campaignRecord.retailer_company_id);
    } else {
      // fetch store companies.
      fetchStoreLevelCompanies("campaigns");
    }
  }, []);
  return (
    <Dialog
      open={open}
      onClose={(e) => {
        e.preventDefault();
      }}
      fullWidth={true}
      maxWidth={size}
      PaperProps={{
        sx: {
          minHeight: dialogExpanded ? "30vh" : "30vh", // dynamic height change (not needed for new MUI DatePicker)
          transition: "min-height 0.15s ease",         // animation
          overflow: 'hidden'
        },
      }}
    >
      <DialogTitle> {usedFor === "update" ? "Update Contact-based Campaign" : "Add New Contact-based Campaign"}</DialogTitle>
      <DialogContent>
        <Box>
          {/* -------- campaign and brand name ------- */}
          <Stack direction={"row"} spacing={4}>
            {/* ------- campaign name -------- */}
            <TextInputField
              fieldRequired={true}
              elemId="campaignName"
              label="Campaign Name"
              fieldName="campaign_name"
              showlabel={true}
              defaultVal={
                typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.campaign_name !== "undefined"
                  ? currentStateData.new_campaign_data.campaign_name
                  : ""
              }
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                handleFieldChange({
                  campaign_name: e.target.value,
                });
              }}
              fieldError={
                typeof fieldErrors.campaign_name !== "undefined" && fieldErrors.campaign_name !== ""
                  ? fieldErrors.campaign_name
                  : ""
              }
            />
            {/* ---------- owner selection -------- */}
            {
              userInfo.user_type === "cyreen" && usedFor !== "update" && (
                <SingleSelect
                  label="Owner*"
                  enableFullWidth={true}
                  elemId={"ownerDropdown"}
                  options={
                    typeof currentStateData.independent_owners_list !== "undefined" ? Object.values(currentStateData.independent_owners_list) : []
                  }
                  selectionCheck={(option, value) => option?.id === value?.id}
                  defaultVal={
                    typeof currentStateData.new_campaign_data !== "undefined" &&
                      typeof currentStateData.new_campaign_data.campaign_owner !== "undefined"
                      ? {
                        label: currentStateData.new_campaign_data.campaign_owner,
                        id: currentStateData.new_campaign_data.campaign_owner_id,
                      }
                      : ""
                  }
                  onChangeCall={(selectedOption) => {
                    let ownerSelections = {
                      campaign_owner: selectedOption.label,
                      campaign_owner_id: selectedOption.id,
                      // campaign_name: "",
                      campaign_start_date: "",
                      campaign_end_date: "",
                      brand_name: "",
                      campaign_brand_company: "",
                      campaign_brand_company_id: "",
                      manual_brand_company: false
                    }
                    fetchBrands(selectedOption.id);
                    handleFieldChange(ownerSelections);
                  }}
                  fieldError={
                    typeof fieldErrors.campaign_owner !== "undefined" && fieldErrors.campaign_owner !== ""
                      ? fieldErrors.campaign_owner
                      : ""
                  }
                />
              )
            }
          </Stack>
          <Box height={40} />
          {/* -------- campaign start/end date ----------- */}
          <Stack direction={"row"} spacing={4}>
            {/* ------ campaign start date -------- */}
            <DateInputField
              fieldRequired={true}
              elemId="startDate"
              label="Start Date*"
              fieldName="start_date"
              className="start_date_class"
              defaultVal={
                typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.campaign_start_date !== "undefined"
                  ? currentStateData.new_campaign_data.campaign_start_date
                  : ""
              }
              showlabel={false}
              inputType={"date"}
              disabled={
                typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.start_status !== "undefined" &&
                  ["started_today", "started_days_ago"].includes(currentStateData.new_campaign_data.start_status)
                  ? true
                  : false
              }
              minSelectable={setCampaignStartDateConstraint()}
              // enabledDates={(date) => date.getDay() === 1}
              onChangeCall={(selectedDate) => {
                // trigger dialog height increase nur wenn Datum gewählt wird
                if (usedFor === "update") {
                  setStartDateConfirmation(true);
                }
                handleFieldChange({
                  campaign_start_date: selectedDate,
                });
              }}
              fieldError={
                typeof fieldErrors.start_date !== "undefined" && fieldErrors.start_date !== ""
                  ? fieldErrors.start_date
                  : ""
              }
              setDialogExpanded={setDialogExpanded}
              onWeekSelect={handleWeekSelect}
              weekSelectionMinDate={weekSelectionMin}
              weekSelectionMaxDate={weekSelectionMax}
            />
            {/* -------- campaign end date ------- */}
            <DateInputField
              fieldRequired={true}
              elemId="endDate"
              label="End Date*"
              fieldName="end_date"
              className="end_date_class"
              defaultVal={
                typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.campaign_end_date !== "undefined"
                  ? currentStateData.new_campaign_data.campaign_end_date
                  : ""
              }
              showlabel={false}
              inputType={"date"}
              minSelectable={(() => {
                if (
                  typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.campaign_start_date !== "undefined" &&
                  moment(currentStateData.new_campaign_data.campaign_start_date).isSameOrAfter(moment(), "day")
                ) {
                  return currentStateData.new_campaign_data.campaign_start_date;
                } else {
                  return setCampaignStartDateConstraint();
                }
              })()}
              // enabledDates={(date) => date.getDay() === 0}
              disabled={
                typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.end_status !== "undefined" &&
                  ["ended_days_ago"].includes(currentStateData.new_campaign_data.end_status)
                  ? true
                  : false
              }
              onChangeCall={(selectedDate) => {
                // trigger dialog height increase nur wenn Datum gewählt wird
                if (usedFor === "update") {
                  setStartEndConfirmation(true);
                } else {
                  setStartEndConfirmation(false);
                }
                handleFieldChange({
                  campaign_end_date: selectedDate,
                });
              }}
              fieldError={
                typeof fieldErrors.end_date !== "undefined" && fieldErrors.end_date !== ""
                  ? fieldErrors.end_date
                  : ""
              }
              setDialogExpanded={setDialogExpanded}
              onWeekSelect={handleWeekSelect}
              weekSelectionMinDate={weekSelectionMin}
              weekSelectionMaxDate={weekSelectionMax}
            />
          </Stack>
          {(showStartDateConfirmation || showEndDateConfirmation) && (
            <>
              <Box height={40} />
              <StyledInfoBox>
                <Typography variant="body2">
                  Changing a campaign’s dates does not automatically update the dates of associated visuals. Please adjust them manually if necessary.
                </Typography>
              </StyledInfoBox>
            </>
          )}

          <Box height={40} />
          {/* -------- retailer/brand company --------- */}
          <Stack direction={"row"} spacing={4}>
            {/* ------ brand name -------- */}
            <TextInputField
              fieldRequired={true}
              elemId="brandName"
              label="Brand Name"
              fieldName="brand_name"
              defaultVal={
                typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.brand_name !== "undefined"
                  ? currentStateData.new_campaign_data.brand_name
                  : ""
              }
              showlabel={true}
              inputType={"text"}
              onChangeCall={(e) => {
                e.preventDefault();
                handleFieldChange({
                  brand_name: e.target.value,
                });
              }}
              fieldError={
                typeof fieldErrors.brand_name !== "undefined" && fieldErrors.brand_name !== ""
                  ? fieldErrors.brand_name
                  : ""
              }
            />
            {/* -------- brand company ------- */}
            <SingleSelect
              label="Brand Company*"
              enableFullWidth={true}
              elemId={"brandCompanyDropdown"}
              options={
                typeof currentStateData.brand_list !== "undefined" ? Object.values(currentStateData.brand_list) : []
              }
              selectionCheck={(option, value) => option?.id === value?.id}
              defaultVal={
                typeof currentStateData.new_campaign_data !== "undefined" &&
                  typeof currentStateData.new_campaign_data.campaign_brand_company !== "undefined"
                  ? {
                    label: currentStateData.new_campaign_data.campaign_brand_company,
                    id: currentStateData.new_campaign_data.campaign_brand_company_id,
                  }
                  : ""
              }
              onChangeCall={(selectedOption) => {
                if (selectedOption.id === "new") {
                  setShowManualBrandCompany(true);
                  handleFieldChange({
                    campaign_brand_company: selectedOption.label,
                    campaign_brand_company_id: selectedOption.id,
                    manual_brand_company: true,
                  });
                } else {
                  setShowManualBrandCompany(false);
                  handleFieldChange({
                    campaign_brand_company: selectedOption.label,
                    campaign_brand_company_id: selectedOption.id,
                    manual_brand_company: false,
                  });
                }
              }}
              fieldError={
                typeof fieldErrors.brand_company !== "undefined" && fieldErrors.brand_company !== ""
                  ? fieldErrors.brand_company
                  : ""
              }
            />
            {/* ------------- manual brand company name ------------ */}
            {showManualBrandCompany && (
              <TextInputField
                fieldRequired={true}
                elemId="brandCompanyName"
                label="Brand Company Name"
                fieldName="brand_company_name"
                defaultVal={
                  typeof currentStateData.new_campaign_data !== "undefined" &&
                    typeof currentStateData.new_campaign_data.campaign_brand_company_name !== "undefined"
                    ? currentStateData.new_campaign_data.campaign_brand_company_name
                    : ""
                }
                showlabel={true}
                inputType={"text"}
                onChangeCall={(e) => {
                  // console.log("BRuder ich raste aus ", currentStateData.new_campaign_data.share_list)
                  e.preventDefault();
                  handleFieldChange({
                    campaign_brand_company_name: e.target.value,
                  });
                }}
                fieldError={
                  typeof fieldErrors.campaign_brand_company_name !== "undefined" &&
                    fieldErrors.campaign_brand_company_name !== ""
                    ? fieldErrors.campaign_brand_company_name
                    : ""
                }
              />
            )}
          </Stack>
          {/* ---------- last modified info ---------- */}
          {usedFor === "update" && (
            <>
              <Box height={40} />
              <StyledDarkRedInformationTypography variant="body2">
                Last Modified:
                <StyledInformationSpan
                  style={{
                    color: "#2D2D37",
                    fontWeight: "bold",
                    marginLeft: "5px",
                  }}
                >
                  {campaignRecord.last_edit}
                </StyledInformationSpan>
              </StyledDarkRedInformationTypography>
            </>
          )}
        </Box>
      </DialogContent>
      <Box height={20} />
      <DialogActions>
        <Stack direction={"row"} spacing={2}>
          <CloseButton
            onClick={(e) => {
              e.preventDefault();
              dispatchAction(deleteItems(["new_campaign_data", "store_level_retailers_list"]));
              closeModal(false);
            }}
            label="Cancel"
            icon={<Close />}
          />
          <ActionButton
            onClick={(e) => {
              e.preventDefault();
              setFieldErrors({});
              saveCampaignsInformation(usedFor).then((info) => {
                if (info.success === false) {
                  setFieldErrors(info.errorMessages);
                } else {
                  closeModal();
                }
              });
            }}
            label={usedFor === "update" ? "Update" : "Save"}
            icon={<Save />}
          />
        </Stack>
      </DialogActions>
    </Dialog >
  );
};

export default AddNewCampaign;
